import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { WhatsappLogo, CheckCircle, Trash, ArrowRight, ChatCircle, ImageSquare } from "@phosphor-icons/react";

export default function WhatsAppInbox() {
  const [items, setItems] = useState([]);
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [targetCase, setTargetCase] = useState("");
  const [filter, setFilter] = useState("pending"); // pending | all

  const load = async () => {
    const [{ data: inb }, { data: cs }] = await Promise.all([
      api.get("/whatsapp/inbox"), api.get("/cases"),
    ]);
    setItems(inb || []);
    setCases(cs || []);
  };
  useEffect(() => { load(); const t = setInterval(load, 15_000); return () => clearInterval(t); }, []);

  const filtered = filter === "all" ? items : items.filter((m) => !m.processed);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const selectAll = () => setSelected(new Set(filtered.map((m) => m.inbox_id)));
  const clearSel = () => setSelected(new Set());

  const promote = async () => {
    if (!targetCase || selected.size === 0) { alert("Pick a case + at least one inbox item"); return; }
    try {
      await api.post("/whatsapp/inbox/bulk-promote", {
        case_id: targetCase, notes: null, inbox_ids: [...selected],
      });
      setSelected(new Set());
      load();
    } catch (e) {
      alert("Bulk promote failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const dismiss = async (id) => {
    if (!window.confirm("Dismiss this item? It will be marked processed.")) return;
    await api.delete(`/whatsapp/inbox/${id}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="whatsapp-inbox-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><WhatsappLogo size={12}/> Channel · WhatsApp</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Inbox</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">
            {items.filter((m) => !m.processed).length} pending · {items.length} total · auto-refresh every 15s
          </div>
        </div>
        <div className="flex gap-2">
          {["pending", "all"].map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`badge ${filter === f ? "badge-success" : ""}`}>{f}</button>
          ))}
        </div>
      </div>

      <div className="card-flat p-3 flex flex-wrap items-center gap-3" data-testid="bulk-bar">
        <div className="text-[12px]">
          <span className="font-mono font-bold">{selected.size}</span> selected
        </div>
        <button onClick={selectAll} disabled={filtered.length === 0} className="span-btn span-btn-ghost h-8 text-[11px]">Select all visible</button>
        <button onClick={clearSel} disabled={selected.size === 0} className="span-btn span-btn-ghost h-8 text-[11px]">Clear</button>
        <select value={targetCase} onChange={(e) => setTargetCase(e.target.value)} className="span-input h-8 text-[11px] flex-1 min-w-[260px]" data-testid="target-case-select">
          <option value="">— Promote to case… —</option>
          {cases.slice(0, 200).map((c) => (
            <option key={c.case_id} value={c.case_id}>{c.case_code} · {c.title?.slice(0, 60)}</option>
          ))}
        </select>
        <button data-testid="bulk-promote-btn" onClick={promote} disabled={!targetCase || selected.size === 0} className="span-btn h-8 text-[11px]">
          <ArrowRight size={12}/> Promote {selected.size} to evidence
        </button>
      </div>

      <div className="card-flat">
        <table className="span-table">
          <thead>
            <tr><th className="w-8"></th><th>From</th><th>Profile</th><th>Body</th><th>Media</th><th>Received</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-[12px] text-[var(--span-text-2)]">
                Inbox is empty. Configure Twilio inbound webhook → <span className="font-mono">/api/webhooks/twilio/whatsapp</span> to start receiving.
              </td></tr>
            )}
            {filtered.map((m) => (
              <tr key={m.inbox_id} data-testid={`inbox-row-${m.inbox_id}`} className={m.processed ? "opacity-50" : ""}>
                <td><input type="checkbox" checked={selected.has(m.inbox_id)} onChange={() => toggle(m.inbox_id)} disabled={m.processed}/></td>
                <td className="font-mono text-[11px]">{m.from?.replace("whatsapp:", "") || "—"}</td>
                <td className="text-[12px]">{m.profile_name || "—"}</td>
                <td className="text-[12px] max-w-md truncate" title={m.body}>{m.body || "—"}</td>
                <td className="text-[11px] text-[var(--span-text-2)] flex items-center gap-1">
                  {m.media_count > 0 ? <><ImageSquare size={11}/> {m.media_count}</> : <ChatCircle size={11}/>}
                </td>
                <td className="text-[var(--span-text-2)] text-[11px]">{fmtDate(m.received_at)}</td>
                <td>
                  {m.processed ? (
                    <span className="badge badge-success text-[10px]"><CheckCircle size={10}/> processed</span>
                  ) : <span className="badge text-[10px]">pending</span>}
                </td>
                <td>
                  {!m.processed && (
                    <button data-testid={`dismiss-${m.inbox_id}`} onClick={() => dismiss(m.inbox_id)} className="text-[var(--span-text-2)] hover:text-[var(--span-danger)]">
                      <Trash size={12}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
